import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/*
 * 返回恢复(back-restore):列表页的页码/筛选/关键词/滚动都只在 useState 里,
 * 任何真实路由跳转(进详情路由页/登录/我的委托…)再返回,组件重挂载会全部重置回
 * 第1页+顶部 —— 这就是「返回被弹回起始点」的根因。
 *
 * 方案:按「pathname+search」把状态快照实时写进 sessionStorage;
 * 仅当本次进入是浏览器历史返回/前进(POP,含整页刷新)时才恢复快照,
 * 正常点击进入(PUSH/REPLACE)仍是全新默认状态,不会串。
 */

const PREFIX = 'yc_back_state:';

/**
 * @returns {[object|null, function]} [restored, save]
 *   restored — POP(返回/刷新)进入时上次离开的快照;全新进入(PUSH)为 null
 *   save(patch) — 合并写入快照(sessionStorage),状态一变就调
 */
export default function useBackRestore() {
  const location = useLocation();
  const navType = useNavigationType();
  // 键含查询串:/browse?keyword=x 与 /browse 各存各的;/sessions/:id/lots 天然按场次分键
  const storageKeyRef = useRef(PREFIX + location.pathname + location.search);
  const storageKey = storageKeyRef.current;

  // 首渲染同步读取一次(必须在 useState initializer 之前拿到)
  const savedRef = useRef(undefined);
  if (savedRef.current === undefined) {
    let saved = null;
    if (navType === 'POP') {
      try { saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { /* 损坏则忽略 */ }
    }
    savedRef.current = saved;
  }

  const snapRef = useRef(savedRef.current || {});
  const save = useCallback((patch) => {
    snapRef.current = { ...snapRef.current, ...patch };
    try { sessionStorage.setItem(storageKey, JSON.stringify(snapRef.current)); } catch { /* 存满则放弃 */ }
  }, [storageKey]);

  return [savedRef.current, save];
}

/**
 * 滚动位置恢复:
 * - 数据首次就绪(ready=true)后恢复一次(双 rAF 等列表画完,否则内容不够高滚不到位);
 * - document 捕获阶段持续监听滚动(200ms 节流)写回快照 —— 同时覆盖 window 滚动页
 *   与内部滚动容器页(如 Browse 的 mainRef),passive 不影响滚动性能。
 * @param {object|null} saved     useBackRestore 返回的快照
 * @param {function}    save      useBackRestore 返回的写入函数
 * @param {boolean}     ready     列表数据已渲染(如 !loading && lots.length > 0)
 * @param {function=}   getScrollEl 返回内部滚动容器;缺省用 window
 */
export function useScrollRestore(saved, save, ready, getScrollEl) {
  const restoredRef = useRef(false);
  const getElRef = useRef(getScrollEl);
  getElRef.current = getScrollEl;

  useEffect(() => {
    if (!ready || restoredRef.current) return;
    restoredRef.current = true;
    const y = saved && saved.scrollY;
    if (!y) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = getElRef.current ? getElRef.current() : null;
      if (el) el.scrollTop = y;
      else window.scrollTo(0, y);
    }));
  }, [ready, saved]);

  useEffect(() => {
    let timer = null;
    const onScroll = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        const el = getElRef.current ? getElRef.current() : null;
        save({ scrollY: el ? el.scrollTop : window.scrollY });
      }, 200);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (timer) clearTimeout(timer);
    };
  }, [save]);
}
